import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Input, Button, Form, Modal, Select, Typography } from "antd";
import {
  CheckOutlined,
  PlayCircleOutlined,
  PictureOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Controller, useForm } from "react-hook-form";
import axios from "axios";
import useToken from "../Authentication/useToken";
import Constants from "../Utils/Constants";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import ReactPlayer from "react-player";
import "./Styles.css";

export default function ExerciseDetail({
  exercise,
  refetchExercises,
  deleteExercise,
  goBack,
}) {
  const { t } = useTranslation("Exercises");
  const { handleSubmit, control, setValue } = useForm();
  const { token } = useToken();

  const [currentExercise, setCurrentExercise] = useState(exercise);
  const [isEditing, setIsEditing] = useState(false);

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  const [message, setMessage] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaveClicked, setIsSaveClicked] = useState(false);

  const [exerciseStatus, setExerciseStatus] = useState(
    exercise.status || "active"
  );

  const [playbackUrl, setPlaybackUrl] = useState(null);

  // MinIO library
  const [videos, setVideos] = useState([]);
  const [images, setImages] = useState([]);

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const [videoSearch, setVideoSearch] = useState("");
  const [imageSearch, setImageSearch] = useState("");

  const [isVideoPreviewOpen, setIsVideoPreviewOpen] = useState(false);

  const videoUrlRegex =
    /^(https?:\/\/)?(www\.)?(vimeo\.com\/\d+|(youtube\.com\/watch\?v=|youtu\.be\/)[\w-]{11})(\?.*)?$/;

  const isMinioVideoKey = (value) =>
    typeof value === "string" && value.startsWith("videos/");

  const isMinioImageKey = (value) =>
    typeof value === "string" && value.startsWith("images/");

  function openModal(msg, isError) {
    setMessage(msg);
    setIsErrorMessage(isError);
    setIsOpenModal(true);
  }

  function closeModal() {
    setIsOpenModal(false);
    setMessage("");
    setIsErrorMessage(false);
    setIsSaveClicked(false);
  }

  useEffect(() => {
    setCurrentExercise(exercise);
    setExerciseStatus(exercise.status || "active");
  }, [exercise]);

  // Generate signed video URL for display
  useEffect(() => {
    const videoUrl = currentExercise?.videoUrl;

    if (!videoUrl) {
      setPlaybackUrl(null);
      return;
    }

    if (isMinioVideoKey(videoUrl)) {
      axios
        .get(`${Constants.SERVER_URL}/api/minio/presign-download`, {
          params: {
            key: videoUrl,
          },
          headers: {
            Authorization: "Bearer " + token,
          },
        })
        .then((res) => {
          setPlaybackUrl(res.data.downloadUrl);
        })
        .catch(() => {
          setPlaybackUrl(null);
        });

      return;
    }

    setPlaybackUrl(videoUrl);
  }, [currentExercise?.videoUrl, token]);

  // Load MinIO library only when editing
  useEffect(() => {
    if (!token || !isEditing) return;

    axios
      .get(`${Constants.SERVER_URL}/api/minio/library?limit=50`, {
        headers: {
          Authorization: "Bearer " + token,
        },
      })
      .then((res) => {
        setVideos(res.data.videos || []);
        setImages(res.data.images || []);
      })
      .catch(() => {
        setVideos([]);
        setImages([]);
      });
  }, [token, isEditing]);

  const filteredVideos = useMemo(() => {
    const query = videoSearch.trim().toLowerCase();

    if (!query) return videos;

    return videos.filter((video) =>
      video.key.toLowerCase().includes(query)
    );
  }, [videos, videoSearch]);

  const filteredImages = useMemo(() => {
    const query = imageSearch.trim().toLowerCase();

    if (!query) return images;

    return images.filter((image) =>
      image.key.toLowerCase().includes(query)
    );
  }, [images, imageSearch]);

  const startEditing = () => {
    setIsEditing(true);

    setValue("name", currentExercise.name);
    setValue("description", currentExercise.description);
    setValue("category", currentExercise.category);
    setValue("fitnessLevel", currentExercise.fitnessLevel);
    setValue("videoUrl", currentExercise.videoUrl);
    setValue("imageUrl", currentExercise.imageUrl);
    setValue(
      "numberOfRepetitions",
      currentExercise.numberOfRepetitions ?? 0
    );

    setExerciseStatus(currentExercise.status || "active");

    setSelectedVideo(
      currentExercise.videoUrl
        ? {
            key: currentExercise.videoUrl,
            url: playbackUrl,
          }
        : null
    );

    setSelectedImage(
      currentExercise.imageUrl
        ? {
            key: currentExercise.imageUrl,
            url: null,
          }
        : null
    );
  };

  const handleDeleteExercise = () => {
    Modal.confirm({
      title: "Supprimer cet exercice ?",
      content:
        "Cette action est irréversible. Voulez-vous vraiment supprimer cet exercice ?",
      okText: "Supprimer",
      cancelText: "Annuler",
      okType: "danger",
      async onOk() {
        try {
          await deleteExercise(currentExercise);
          goBack();
        } catch (error) {
          // Le message d'erreur est déjà géré dans ExerciseMenu
        }
      },
    });
  };

  const onSubmit = (data) => {
    if (!isSaveClicked) return;

    const finalVideoUrl = selectedVideo?.key || currentExercise.videoUrl;
    const finalImageKey = selectedImage?.key || currentExercise.imageUrl;

    if (
      finalVideoUrl &&
      !isMinioVideoKey(finalVideoUrl) &&
      !videoUrlRegex.test(finalVideoUrl)
    ) {
      openModal(t("invalid_video_link"), true);
      return;
    }

    if (finalImageKey && !isMinioImageKey(finalImageKey)) {
      openModal("Image MinIO invalide.", true);
      return;
    }

    const payload = {
      name: data.name,
      description: data.description,
      category: data.category,
      fitnessLevel: data.fitnessLevel,
      numberOfRepetitions: Number(data.numberOfRepetitions ?? 0),
      status: exerciseStatus,
      videoUrl: finalVideoUrl,
      imageKey: finalImageKey,
    };

    const hasChanged =
      currentExercise.name !== payload.name ||
      currentExercise.description !== payload.description ||
      currentExercise.category !== payload.category ||
      currentExercise.fitnessLevel !== payload.fitnessLevel ||
      Number(currentExercise.numberOfRepetitions ?? 0) !==
        payload.numberOfRepetitions ||
      (currentExercise.status || "active") !== payload.status ||
      currentExercise.videoUrl !== payload.videoUrl ||
      currentExercise.imageUrl !== payload.imageKey;

    if (!hasChanged) {
      openModal(t("error_no_changes_detected"), true);
      setIsEditing(false);
      setIsSaveClicked(false);
      return;
    }

    setIsSubmitting(true);

    axios
      .put(
        `${Constants.SERVER_URL}/update-exercise/${currentExercise.key}`,
        payload,
        {
          headers: {
            Authorization: "Bearer " + token,
          },
        }
      )
      .then(async () => {
  setCurrentExercise((prev) => ({
    ...prev,
    ...payload,
    imageUrl: payload.imageKey,
  }));

  await refetchExercises();

  setIsSubmitting(false);
  setIsEditing(false);
  setIsSaveClicked(false);

  goBack();
})
      .catch((err) => {
        openModal(
          t(`Backend:${err.response?.data?.message}`) ||
            t("error_unknown"),
          true
        );

        setIsSubmitting(false);
        setIsSaveClicked(false);
      });
  };

  return (
    <div className="exercise-detail-container">
      <Typography.Title level={1} className="exercise-title">
        {currentExercise.name}
      </Typography.Title>

      <Row gutter={40} style={{ width: "100%" }}>
        <Col span={12} className="video-container">
          {playbackUrl ? (
            <div className="video-wrapper">
              <ReactPlayer
                url={playbackUrl}
                controls
                width="100%"
                height="100%"
                className="react-player"
              />
            </div>
          ) : (
            <div style={{ padding: 12, color: "#999" }}>
              Aucune vidéo disponible.
            </div>
          )}
        </Col>

        <Col span={12} className="info-container">
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <Form.Item
              label={t("placeholder_exercise_name")}
              style={{ fontWeight: "bold" }}
            >
              <Controller
                name="name"
                control={control}
                defaultValue={currentExercise.name}
                render={({ field }) => (
                  <Input {...field} required disabled={!isEditing} />
                )}
              />
            </Form.Item>

            <Form.Item
              label={t("placeholder_exercise_category")}
              style={{ fontWeight: "bold" }}
            >
              <Controller
                name="category"
                control={control}
                defaultValue={currentExercise.category}
                render={({ field }) => (
                  <Select
                    {...field}
                    style={{ width: "100%" }}
                    allowClear
                    disabled={!isEditing}
                  >
                    {[
                      t("option_aerobic"),
                      t("option_strength"),
                      t("option_endurance"),
                      t("option_flexibility"),
                      t("option_balance"),
                    ].map((category) => (
                      <Select.Option key={category} value={category}>
                        {category}
                      </Select.Option>
                    ))}
                  </Select>
                )}
              />
            </Form.Item>

            <Form.Item
              label={t("fitness_level_placeholder")}
              style={{ fontWeight: "bold" }}
            >
              <Controller
                name="fitnessLevel"
                control={control}
                defaultValue={currentExercise.fitnessLevel}
                render={({ field }) => (
                  <Select
                    {...field}
                    style={{ width: "100%" }}
                    allowClear
                    disabled={!isEditing}
                  >
                    {[
                      t("option_easy"),
                      t("option_intermediate"),
                      t("option_advanced"),
                    ].map((level) => (
                      <Select.Option key={level} value={level}>
                        {level}
                      </Select.Option>
                    ))}
                  </Select>
                )}
              />
            </Form.Item>

            <Form.Item
              label={t("label_number_of_reps")}
              style={{ fontWeight: "bold" }}
            >
              <Controller
                name="numberOfRepetitions"
                control={control}
                defaultValue={currentExercise.numberOfRepetitions ?? 0}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    required
                    disabled={!isEditing}
                    onChange={(e) =>
                      field.onChange(Number(e.target.value))
                    }
                  />
                )}
              />
            </Form.Item>

            <Form.Item
              label={t("placeholder_exercise_description")}
              style={{ fontWeight: "bold" }}
            >
              <Controller
                name="description"
                control={control}
                defaultValue={currentExercise.description}
                render={({ field }) => (
                  <Input.TextArea
                    {...field}
                    rows={4}
                    required
                    disabled={!isEditing}
                  />
                )}
              />
            </Form.Item>

            {!isEditing && (
              <>
                <Form.Item
                  label="Vidéo d'instruction de l'exercice"
                  style={{ fontWeight: "bold" }}
                >
                  <Input value={currentExercise.videoUrl || ""} disabled />
                </Form.Item>

                <Form.Item
                  label="Image de l'exercice"
                  style={{ fontWeight: "bold" }}
                >
                  <Input value={currentExercise.imageUrl || ""} disabled />
                </Form.Item>
              </>
            )}

            {isEditing && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Typography.Text strong>
                    <PlayCircleOutlined /> Choisir une vidéo MinIO
                  </Typography.Text>

                  <Input
                    style={{ marginTop: 8, marginBottom: 8 }}
                    placeholder="Rechercher une vidéo..."
                    value={videoSearch}
                    onChange={(e) => setVideoSearch(e.target.value)}
                  />

                  <div
                    style={{
                      maxHeight: 200,
                      overflow: "auto",
                      border: "1px solid #eee",
                      borderRadius: 8,
                    }}
                  >
                    {filteredVideos.map((video) => (
                      <div
                        key={video.key}
                        onClick={() => setSelectedVideo(video)}
                        style={{
                          padding: 10,
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          borderBottom: "1px solid #f3f3f3",
                          background:
                            selectedVideo?.key === video.key
                              ? "#e6f4ff"
                              : "white",
                        }}
                      >
                        <span>{video.key}</span>

                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedVideo(video);
                            setIsVideoPreviewOpen(true);
                          }}
                        >
                          Aperçu
                        </Button>
                      </div>
                    ))}

                    {!filteredVideos.length && (
                      <div style={{ padding: 10, color: "#999" }}>
                        Aucune vidéo
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <Typography.Text strong>
                    <PictureOutlined /> Choisir une image MinIO
                  </Typography.Text>

                  <Input
                    style={{ marginTop: 8, marginBottom: 8 }}
                    placeholder="Rechercher une image..."
                    value={imageSearch}
                    onChange={(e) => setImageSearch(e.target.value)}
                  />

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: 10,
                      maxHeight: 260,
                      overflow: "auto",
                      padding: 10,
                      border: "1px solid #eee",
                      borderRadius: 8,
                    }}
                  >
                    {filteredImages.map((image) => (
                      <div
                        key={image.key}
                        onClick={() => setSelectedImage(image)}
                        style={{
                          border:
                            selectedImage?.key === image.key
                              ? "2px solid #1677ff"
                              : "1px solid #ddd",
                          borderRadius: 10,
                          overflow: "hidden",
                          cursor: "pointer",
                          background: "white",
                        }}
                        title={image.key}
                      >
                        <img
                          src={image.url}
                          alt={image.key}
                          style={{
                            width: "100%",
                            height: 90,
                            objectFit: "cover",
                            display: "block",
                          }}
                        />

                        <div
                          style={{
                            padding: 6,
                            fontSize: 11,
                            color: "#555",
                          }}
                        >
                          {image.key.replace("images/", "")}
                        </div>
                      </div>
                    ))}

                    {!filteredImages.length && (
                      <div style={{ color: "#999" }}>Aucune image</div>
                    )}
                  </div>
                </div>
              </>
            )}

            <Form.Item
              label={t("placeholder_exercise_status")}
              style={{ fontWeight: "bold" }}
            >
              <Select
                value={exerciseStatus}
                onChange={(value) => setExerciseStatus(value)}
                disabled={!isEditing}
                style={{ width: "100%" }}
              >
                <Select.Option value="active">
                  {t("option_active")}
                </Select.Option>
                <Select.Option value="inactive">
                  {t("option_inactive")}
                </Select.Option>
              </Select>
            </Form.Item>

            <Form.Item>
              {!isEditing ? (
                <div style={{ display: "flex", gap: 12 }}>
                  <Button type="primary" onClick={startEditing}>
                    {t("button_modify")}
                  </Button>

                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleDeleteExercise}
                  >
                    Supprimer
                  </Button>
                </div>
              ) : (
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<CheckOutlined />}
                  loading={isSubmitting}
                  onClick={() => setIsSaveClicked(true)}
                >
                  {t("button_save")}
                </Button>
              )}
            </Form.Item>
          </Form>

          <Modal
            open={isVideoPreviewOpen}
            onCancel={() => setIsVideoPreviewOpen(false)}
            footer={null}
            width={800}
            title="Aperçu vidéo"
          >
            {selectedVideo?.url ? (
              <ReactPlayer url={selectedVideo.url} controls width="100%" />
            ) : (
              <p>Aucune vidéo sélectionnée.</p>
            )}
          </Modal>

          {isOpenModal && (
            <Modal
              title={t("Feedback")}
              open={isOpenModal}
              onCancel={closeModal}
              footer={[
                <Button key="close" onClick={closeModal}>
                  {t("button_close")}
                </Button>,
              ]}
            >
              <p style={{ color: isErrorMessage ? "red" : "green" }}>
                {message}
              </p>
            </Modal>
          )}
        </Col>
      </Row>
    </div>
  );
}

ExerciseDetail.propTypes = {
  exercise: PropTypes.shape({
    key: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    category: PropTypes.string.isRequired,
    fitnessLevel: PropTypes.string.isRequired,
    videoUrl: PropTypes.string,
    imageUrl: PropTypes.string,
    status: PropTypes.string,
    numberOfRepetitions: PropTypes.number,
  }).isRequired,
  refetchExercises: PropTypes.func.isRequired,
  deleteExercise: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
};